import { motion } from 'framer-motion'
import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { Play, Eye, Download, Clock, Database, Loader2, AlertCircle, Camera } from 'lucide-react'
import { ModelViewer, resolveModelSource } from '../ModelViewer'
import { VideoPreview } from '../VideoPreview'
import { useModels, useCategories, useRecordView } from '@/hooks/useModels'
import { modelsApi } from '@/services/api/models'
import type { Model, ModelFilters } from '@/types/models'
import type { ShowcaseAnimation } from '@/types/camera'
import { migrateCameraState, cameraStateToArrays, cameraStateToQuaternion } from '@/utils/cameraConversion'
import { useCameraAnimation } from '@/utils/cameraAnimation'
// @ts-expect-error - JSX module without type declarations
import { aiEnhanceCanvas, downloadCanvasScreenshot } from '@/utils/captureTools.jsx'

export function Gallery() {
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [selectedModel, setSelectedModel] = useState<Model | null>(null)
  const viewerCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const dualgsViewerRef = useRef<{
    aiEnhance: () => Promise<void>
    takeScreenshot: () => void
  } | null>(null)
  const [isEnhancing, setIsEnhancing] = useState(false)
  const [filters, setFilters] = useState<ModelFilters>({
    sort: 'newest',
    limit: 20,
  })

  // Fetch data
  const { data: categoriesData, isLoading: categoriesLoading } = useCategories()
  const { data: modelsData, isLoading: modelsLoading, error } = useModels(filters)
  const recordView = useRecordView()

  // Update filters when category changes
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      category: selectedCategory === 'all' ? undefined : selectedCategory,
    }))
  }, [selectedCategory])

  // Format file size
  const formatFileSize = (bytes: number) => {
    const mb = bytes / (1024 * 1024)
    return `${mb.toFixed(0)} MB`
  }

  // Format vertices count
  const formatVertices = (count?: number) => {
    if (!count) return 'N/A'
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`
    if (count >= 1000) return `${(count / 1000).toFixed(0)}K`
    return count.toString()
  }

  // Handle model view
  const handleModelView = (model: Model) => {
    setSelectedModel(model)
    viewerCanvasRef.current = null // Reset canvas reference
    recordView.mutate({ modelId: model.id })
  }
  
  // Handle closing modal
  const handleCloseModal = () => {
    setSelectedModel(null)
    viewerCanvasRef.current = null // Reset canvas reference
  }
  
  // Routing (single / ply / sequence / clipgstream) + URL derivation all live
  // in resolveModelSource; ModelViewer picks the right viewer from this.
  const source = useMemo(
    () => (selectedModel ? resolveModelSource(selectedModel) : null),
    [selectedModel?.id, selectedModel?.filePath, selectedModel?.metadata],
  )

  const { defaultCamera, modelTransform, autoFocus } = useMemo(() => {
    if (!selectedModel?.metadata) {
      return { defaultCamera: undefined, modelTransform: undefined, autoFocus: true }
    }
    
    try {
      const metadata = typeof selectedModel.metadata === 'object' 
        ? selectedModel.metadata 
        : (typeof selectedModel.metadata === 'string' 
          ? JSON.parse(selectedModel.metadata) 
          : {})
      
      const camera = metadata.defaultCamera ? migrateCameraState(metadata.defaultCamera) : undefined
      
      return {
        defaultCamera: camera,
        modelTransform: metadata.modelTransform,
        autoFocus: !camera
      }
    } catch (e) {
      console.error('[Gallery] Failed to parse metadata:', e)
      return { defaultCamera: undefined, modelTransform: undefined, autoFocus: true }
    }
  }, [selectedModel?.metadata])

  // DualgsViewer kinds and FourDViewer expose screenshot / AI-enhance (rendered
  // at 1920×1080) through the forwarded ref.
  const isPlyModel = source?.kind === 'sequence' || source?.kind === 'ply' || source?.kind === 'fourdv'

  // Showcase animation from metadata (with keyframe migration to quaternion form)
  const showcaseAnimation = useMemo<ShowcaseAnimation | undefined>(() => {
    if (!selectedModel?.metadata) return undefined
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let meta: any = selectedModel.metadata
    if (typeof meta === 'string') {
      try { meta = JSON.parse(meta) } catch { return undefined }
    }
    const sa = meta?.showcaseAnimation
    if (!sa?.keyframes?.length) return undefined
    return {
      ...sa,
      // Gallery showcases always loop regardless of the admin's saved setting.
      loop: true,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      keyframes: sa.keyframes.map((kf: any) => ({
        ...kf,
        camera: migrateCameraState(kf.camera)
      }))
    }
  }, [selectedModel?.metadata])

  const animationEnabled = !!showcaseAnimation
  // Drive both viewers from one external hook so loop/interrupt behavior is
  // unified. SplatViewer's internal pauseAnimationOnInteraction triggers on
  // mere hover, which makes animations look like they don't loop when the
  // pointer rests inside the viewer — bypass it.
  const useExternalAnimation = animationEnabled
  const {
    currentCamera: animatedCamera,
    currentTime,
    play: playAnimation,
    pause: pauseAnimation,
    reset: resetAnimation,
  } = useCameraAnimation(
    showcaseAnimation?.keyframes ?? [],
    showcaseAnimation?.duration ?? 0,
    showcaseAnimation?.loop ?? false,
    useExternalAnimation
  )

  // Synchronous gate so the next animation rAF can't race a state update; the
  // state mirror is what triggers the re-render that drops the camera prop.
  const userInterruptedRef = useRef(false)
  const [userInterrupted, setUserInterrupted] = useState(false)

  // Reset everything when the user opens a different model. The hook seeds
  // isPlaying from `enabled` only at mount, so we explicitly reset + play.
  useEffect(() => {
    userInterruptedRef.current = false
    setUserInterrupted(false)
    if (useExternalAnimation && selectedModel) {
      resetAnimation()
      playAnimation()
    }
    // resetAnimation/playAnimation are stable useCallbacks
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModel?.id, useExternalAnimation])

  // Pointer-down / wheel fires before OrbitControls processes the input, so we
  // can flip state in the same render the user's first move lands in.
  const handleViewerInteract = useCallback(() => {
    if (userInterruptedRef.current) return
    userInterruptedRef.current = true
    if (useExternalAnimation) pauseAnimation()
    setUserInterrupted(true)
  }, [useExternalAnimation, pauseAnimation])

  // Stable refs for the viewer's external camera sync — recomputing on every
  // render would re-fire the effect and override user drag.
  const dualgsCameraProps = useMemo(() => {
    if (!defaultCamera) return null
    return cameraStateToQuaternion(defaultCamera)
  }, [defaultCamera])

  // Animated camera tuple — keyed on currentTime so paused state yields a stable
  // reference (viewer effect won't re-fire and overwrite the user's drag).
  const animatedCameraProps = useMemo(() => {
    if (!useExternalAnimation || userInterrupted) return null
    return cameraStateToQuaternion(animatedCamera)
    // animatedCamera identity changes each render; currentTime is the real driver.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useExternalAnimation, userInterrupted, currentTime])

  // After interaction we pass nothing so the viewer keeps the user's last view.
  // Otherwise: animation > static default.
  const activeCameraProps = userInterrupted
    ? null
    : (animatedCameraProps ?? dualgsCameraProps)

  // Screenshot — DualgsViewer handles its own renderer-resize for high-res output;
  // SplatViewer captures at the canvas's current size.
  // Hold Ctrl/Cmd while clicking to run AI Enhance instead of a plain screenshot.
  const handleScreenshot = (e?: React.MouseEvent) => {
    if (e && (e.ctrlKey || e.metaKey)) {
      void handleAiEnhance()
      return
    }
    if (isPlyModel) {
      dualgsViewerRef.current?.takeScreenshot()
    } else if (viewerCanvasRef.current) {
      downloadCanvasScreenshot(viewerCanvasRef.current)
    }
  }

  const handleAiEnhance = async () => {
    if (isEnhancing) return
    setIsEnhancing(true)
    try {
      if (isPlyModel) {
        await dualgsViewerRef.current?.aiEnhance()
      } else if (viewerCanvasRef.current) {
        await aiEnhanceCanvas(viewerCanvasRef.current)
      }
    } catch (err) {
      console.error('AI enhance failed:', err)
      alert(`AI enhance failed: ${(err as Error).message}`)
    } finally {
      setIsEnhancing(false)
    }
  }

  // Handle download
  const handleDownload = async (model: Model) => {
    const url = modelsApi.getDownloadUrl(model.id)
    const link = document.createElement('a')
    link.href = url
    // Keep the stored file's real extension (.splatv / .ply / .4dv / ...).
    const ext = model.filePath?.match(/\.[^./]+$/)?.[0] ?? '.splatv'
    link.download = `${model.name}${ext}`
    link.click()
  }

  if (error) {
    console.error('Error loading models:', error)
    return (
      <section id="gallery" className="py-20 lg:py-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">Error Loading Models</h3>
            <p className="text-gray-400">Please try again later</p>
            {import.meta.env.DEV && (
              <p className="text-xs text-gray-500 mt-2">{(error as Error).message}</p>
            )}
          </div>
        </div>
      </section>
    )
  }

  const categories = [
    { slug: 'all', name: 'All' },
    ...(categoriesData?.data || [])
  ]
  const models = modelsData?.data || []

  return (
    <section id="gallery" className="py-20 lg:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
          >
            Model Gallery
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg text-gray-300 max-w-2xl mx-auto mb-8"
          >
            Explore our collection of high-quality 4D Gaussian Splatting models across various industries
          </motion.p>

          {/* Category Filter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-wrap justify-center gap-2"
          >
            {categoriesLoading ? (
              <div className="flex items-center gap-2 text-gray-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Loading categories...</span>
              </div>
            ) : (
              categories.map((category) => (
                <button
                  key={category.slug}
                  onClick={() => setSelectedCategory(category.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                    selectedCategory === category.slug
                      ? 'bg-gradient-to-r from-primary-500 to-accent-600 text-white'
                      : 'bg-gray-800/50 text-gray-300 hover:bg-gray-700/50'
                  }`}
                >
                  {category.name}
                  {category.slug !== 'all' && 'model_count' in category && (
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    <span className="ml-1 text-xs opacity-70">({(category as any).model_count})</span>
                  )}
                </button>
              ))
            )}
          </motion.div>
        </div>

        {/* Model Grid */}
        {modelsLoading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
          </div>
        ) : models.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-400">No models found in this category</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {models.map((model, index) => (
              <motion.div
                key={model.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group cursor-pointer"
                onClick={() => handleModelView(model)}
              >
                <div className="relative overflow-hidden rounded-xl bg-gray-800/50 border border-gray-700/50 hover:border-gray-600/50 transition-all duration-300">
                  {/* Thumbnail */}
                  <div className="aspect-[4/3] bg-gray-700 relative overflow-hidden">
                    {model.previewVideoPath ? (
                      <VideoPreview
                        src={`${import.meta.env.VITE_API_URL}/api/v2/models/${model.id}/media?path=${encodeURIComponent(model.previewVideoPath)}`}
                        poster={
                          model.thumbnailPath 
                            ? modelsApi.getThumbnailUrl(model.id)
                            : `${import.meta.env.VITE_API_URL}/api/placeholder/400/300`
                        }
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <img
                        src={
                          model.thumbnailPath 
                            ? modelsApi.getThumbnailUrl(model.id)
                            : `${import.meta.env.VITE_API_URL}/api/placeholder/400/300`
                        }
                        alt={model.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement
                          target.src = `${import.meta.env.VITE_API_URL}/api/placeholder/400/300`
                        }}
                      />
                    )}
                    {model.featured && (
                      <div className="absolute top-4 left-4 px-3 py-1 bg-gradient-to-r from-primary-500 to-accent-600 text-white text-xs font-medium rounded-full z-20">
                        Featured
                      </div>
                    )}
                    {/* Play button only shows when no preview video */}
                    {!model.previewVideoPath && (
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/40 z-30">
                        <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center transform scale-90 group-hover:scale-100 transition-transform duration-300">
                          <Play className="w-8 h-8 text-white ml-1" />
                        </div>
                      </div>
                    )}
                    {model.viewCount > 0 && (
                      <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/50 backdrop-blur-sm rounded-full text-xs text-white flex items-center gap-1 z-20">
                        <Eye className="w-3 h-3" />
                        {model.viewCount}
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-4">
                    <h3 className="text-lg font-semibold text-white mb-1 group-hover:text-primary-400 transition-colors">
                      {model.name}
                    </h3>
                    <p className="text-sm text-gray-400 mb-3 line-clamp-2">
                      {model.description || model.category}
                    </p>
                    
                    <div className="grid grid-cols-3 gap-2 text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <Database size={12} />
                        <span>{formatFileSize(model.fileSize)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock size={12} />
                        <span>{model.duration ? `${Math.round(model.duration * 10) / 10}s` : 'N/A'}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Eye size={12} />
                        <span>{formatVertices(model.vertices)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Load More */}
        {modelsData?.pagination.hasMore && (
          <div className="mt-8 text-center">
            <button
              onClick={() => setFilters(prev => ({
                ...prev,
                offset: (prev.offset || 0) + (prev.limit || 20),
              }))}
              className="px-6 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Load More
            </button>
          </div>
        )}

        {/* Model Viewer Modal */}
        {selectedModel && (
          <div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={handleCloseModal}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-gray-900 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-800">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold text-white">{selectedModel.name}</h3>
                    <p className="text-gray-400">{selectedModel.category}</p>
                  </div>
                  <button
                    onClick={handleCloseModal}
                    className="text-gray-400 hover:text-white transition-colors text-2xl"
                  >
                    ✕
                  </button>
                </div>
              </div>
              
              <div
                className="aspect-video bg-gray-800"
                onPointerDownCapture={handleViewerInteract}
                onMouseDownCapture={handleViewerInteract}
                onTouchStartCapture={handleViewerInteract}
                onWheelCapture={handleViewerInteract}
              >
                {source && (
                  <ModelViewer
                    ref={dualgsViewerRef}
                    source={source}
                    className="w-full h-full"
                    cameraPosition={activeCameraProps?.position ?? null}
                    cameraQuaternion={activeCameraProps?.quaternion ?? null}
                    onCameraChange={handleViewerInteract}
                    onCanvasReady={(canvas) => {
                      viewerCanvasRef.current = canvas
                    }}
                    dualgs={{
                      showUi: false,
                      showTimeline: true,
                      // XR wherever a sequence gets it: a .4dv too (ModelViewer forwards it)
                      enableXR: source.kind === 'sequence' || source.kind === 'fourdv',
                    }}
                    splat={{
                      controls: true,
                      quality: 'high',
                      defaultCamera,
                      modelTransform,
                      autoFocus: autoFocus && !animationEnabled,
                      xrEnabled: selectedModel.xrEnabled || false,
                      onLoad: () => {
                        // External hook drives the animation; apply the static default
                        // here only when there's no animation.
                        if (animationEnabled || !defaultCamera) return
                        setTimeout(() => {
                          const canvas = viewerCanvasRef.current
                          if (canvas) {
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            const renderer = (canvas as any)?.__splatRenderer
                            if (renderer && 'setCamera' in renderer) {
                              const arrays = cameraStateToArrays(defaultCamera)
                              renderer.setCamera(arrays.position, arrays.rotation)
                            }
                          }
                        }, 150)
                      },
                    }}
                  />
                )}
              </div>
              
              <div className="p-6 bg-gray-800/50">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex gap-3 text-sm text-gray-400 min-w-0 overflow-hidden whitespace-nowrap">
                    <span>Size: {formatFileSize(selectedModel.fileSize)}</span>
                    <span>Duration: {selectedModel.duration ? `${Math.round(selectedModel.duration * 10) / 10}s` : 'N/A'}</span>
                    <span>Vertices: {formatVertices(selectedModel.vertices)}</span>
                    <span>Views: {selectedModel.viewCount}</span>
                  </div>
                  <div className="flex gap-3 flex-shrink-0">
                    {/* {selectedModel.xrEnabled && (
                      <button className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center gap-2">
                        <Eye size={16} />
                        View in AR
                      </button>
                    )} */}
                    <button
                      onClick={handleScreenshot}
                      disabled={isEnhancing}
                      title="Click: Screenshot · Ctrl/⌘ + Click: AI Enhance"
                      className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {isEnhancing ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Camera size={16} />
                      )}
                      {isEnhancing ? 'Enhancing…' : 'Screenshot'}
                    </button>
                    <button
                      onClick={() => handleDownload(selectedModel)}
                      className="px-4 py-2 bg-gradient-to-r from-primary-500 to-accent-600 text-white rounded-lg hover:shadow-lg hover:shadow-primary-500/25 transition-all flex items-center gap-2"
                    >
                      <Download size={16} />
                      Download Model
                    </button>
                  </div>
                </div>
                {selectedModel.description && (
                  <p className="mt-4 text-gray-300">{selectedModel.description}</p>
                )}
                {selectedModel.tags && selectedModel.tags.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                    {JSON.parse(selectedModel.tags as any).map((tag: string) => (
                      <span key={tag} className="px-2 py-1 bg-gray-700 text-gray-300 rounded-full text-xs">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </section>
  )
}